import { dateToReportId, getReportMonths, stringToDate } from "./date";
import { formatDecimalPlacesNumerical } from "./number";
import { PolicyFrequency } from "../entities/projectPolicy";
export const PRECISION_THRESHOLD = 0.0001;
export const calculateLocalContentPackageValueTotal = (localContents) => {
    return localContents.reduce((acc, lc) => acc + lc.value, 0);
};
/**
 * Local value-add across a package's local-content lines, weighted by each
 * line's share of total contract value.
 *
 * Unlike {@link calculateTargetValueRealised}, there is **no zero guard**: a
 * non-empty list whose values sum to 0 divides by zero and returns `NaN`. An
 * empty list is safe — it returns 0 without ever dividing — so it is the
 * all-zero-values case that bites.
 */
export const calculateLocalValueAddedActivity = (localContents) => {
    const totalValue = localContents.reduce((acc, lc) => acc + (lc.value || 0), 0);
    return localContents.reduce((result, lc) => {
        const mappedContractContent = (lc.value / totalValue) * 100;
        return (result + (lc.localValueAdd / 100) * (mappedContractContent / 100) * 100);
    }, 0);
};
/** Absolute value from a **percentage** `targetValue`; see the inverse below on rounding. */
export const calculateTargetValueRealised = (targetValue, baseMultiplier) => {
    if (baseMultiplier === 0) {
        return 0;
    }
    return formatDecimalPlacesNumerical(baseMultiplier * (targetValue / 100));
};
/**
 * **Percentage** target from an absolute value — the inverse of
 * {@link calculateTargetValueRealised}, but rounded to 4dp rather than 2dp, so
 * putting a value through both directions is lossy.
 */
export const calculateTargetValueFromTargetValueRealised = (targetValueRealised, baseMultiplier) => {
    if (baseMultiplier === 0) {
        return 0;
    }
    return formatDecimalPlacesNumerical((targetValueRealised / baseMultiplier) * 100, 4);
};
export const calcRequiredSpend = (mpsgHoursRequirement, requiredHours) => {
    return (mpsgHoursRequirement / 100) * requiredHours;
};
export const calcRequiredDeemedHours = (availableSpend, deemedLabourRatio, deemedLabourHourlyRate) => {
    if (deemedLabourHourlyRate === 0) {
        return 0;
    }
    return (availableSpend * deemedLabourRatio) / 100 / deemedLabourHourlyRate;
};
export const calcLocalContent = (importValueAdd) => {
    return 100 - importValueAdd;
};
export const calcANZValueAdd = (localValueAdd, contractContent) => {
    return (localValueAdd / 100) * contractContent;
};
export const calcRegionalValueAdd = (regionalValueAdd, contractContent) => {
    return (regionalValueAdd / 100) * contractContent;
};
export const calcDistrictValueAdd = (districtValueAdd, contractContent) => {
    return (districtValueAdd / 100) * contractContent;
};
/**
 * The reporting periods a policy is actually due in, between two dates.
 *
 * The count alone (`calculateExpectedPolicySubmissions`) cannot say *which*
 * periods, and "which" is what tells an outstanding period from an overdue
 * one. Returned as `report_id`s (month-end, `YYYY-MM-DD`) in date order, so
 * they compare directly against a submission's `report_id`.
 */
export const policyDuePeriodIds = (startDate, endDate, frequency, 
/**
 * Report ids a head contractor skipped. A monthly period in a skipped month
 * is not owed at all; a one-time, quarterly or annual period carries to the
 * next month that is not skipped — the same thing the report's due list does.
 */
skippedReportIds = new Set()) => {
    // Whole calendar months at both ends: a package starting on the 30th owes
    // that month like any other, and owes the month currently in progress.
    // `getReportMonths` steps a day-of-month cursor, so an unnormalised range
    // silently drops both.
    const months = getReportMonths(new Date(startDate.getFullYear(), startDate.getMonth(), 1), new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0));
    if (months.length === 0) {
        return [];
    }
    const monthIds = months.map(dateToReportId);
    let due;
    switch (frequency) {
        case PolicyFrequency.OneTime:
            due = [monthIds[0]];
            break;
        case PolicyFrequency.Monthly:
            // A skipped month carries onto the next month, already owed, so it
            // merges away below.
            due = monthIds;
            break;
        case PolicyFrequency.Quarterly:
            due = monthIds.filter((_, index) => index % 3 === 0);
            break;
        case PolicyFrequency.HalfYearly:
            due = monthIds.filter((_, index) => index % 6 === 0);
            break;
        case PolicyFrequency.Annually:
            // Every anniversary of the first reporting month.
            due = monthIds.filter((_, index) => months[index].getMonth() === months[0].getMonth());
            break;
        default:
            return [];
    }
    // Carry each period to the first month from it that is not skipped; drop it
    // when every later month in range is skipped. Two periods carried into the
    // same month are one obligation there.
    const carried = due
        .map((id) => monthIds.find((month) => month >= id && !skippedReportIds.has(month)))
        .filter((id) => id !== undefined);
    return [...new Set(carried)];
};
/**
 * What a policy owes from `startDate` to `endDate`: the calendar periods
 * `policyDuePeriodIds` names, so the count and the report's due list agree. A
 * 30-day estimate used to stand in for months, quarters and years, and owed a
 * project that started late in August one monthly filing on 25 September while
 * its reports listed two.
 */
export const calculateExpectedPolicySubmissions = (startDate, endDate, frequency, isPolicyEnabled = true, 
/** Report ids a head contractor skipped — see `policyDuePeriodIds`. */
skippedReportIds = new Set()) => {
    if (!isPolicyEnabled) {
        return 0;
    }
    // One filing, however far along: owed before the first month too, unless
    // skipping has removed every month it could carry to.
    if (frequency === PolicyFrequency.OneTime && skippedReportIds.size === 0) {
        return 1;
    }
    return policyDuePeriodIds(startDate, endDate, frequency, skippedReportIds)
        .length;
};
export const calculateExpectedReportSubmissions = (startDate) => {
    const startDateDate = stringToDate(startDate);
    const allReportMonths = getReportMonths(startDateDate);
    return allReportMonths.length;
};
export const calculateExpectedReportSubmissionsByToday = (startDate, closedDate) => {
    const today = new Date();
    const startDateDate = stringToDate(startDate);
    const closedDateDate = closedDate ? stringToDate(closedDate) : undefined;
    // Get all report months between start and end date
    const allReportMonths = getReportMonths(startDateDate, closedDateDate);
    // Filter only the months that are before or equal to the current month
    // We subtract 1 month because the current month's report is not yet due
    const today1MonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
    return allReportMonths.filter((reportMonth) => reportMonth <= today1MonthAgo)
        .length;
};
export const calculateExpectedReportSubmissionsByTodayForProject = (startDate) => {
    const today = new Date();
    const startDateDate = stringToDate(startDate);
    // Get all report months between start and end date
    const allReportMonths = getReportMonths(startDateDate);
    // Filter only the months that are before or equal to the current month
    // We subtract 1 month because the current month's report is not yet due
    const today1MonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
    return allReportMonths.filter((reportMonth) => reportMonth <= today1MonthAgo)
        .length;
};
